import { useEffect, useState } from "react";
import { Button, ConfirmationDialog, RenderName, TableHeader, TableRow, TextInput, Pagination } from "../../components";
import { officeHeaders } from "./OfficeScreenData";
import { Office, OfficeState } from "../../models/office.model";
import { useDispatch } from "react-redux";
import { AppDispatch, RootState } from "../../redux/store";
import { getAllOffices, sortObj, updateOfficeState } from "../../redux/slices/OfficeSlice";
import { useSelector } from "react-redux";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import { useDebounce } from "../../components/useDebounce";

interface AllOfficeProps {
  searchTerm?: string;
}

const AllOffice = ({ searchTerm = "" }: AllOfficeProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const [showVerifyDialog, setShowVerifyDialog] = useState<Office | undefined>(undefined);
  const [showDenyReason, setShowDenyReason] = useState(false);
  const [denyReason, setDenyReason] = useState("");
  const [extraClasses] = useState<string[]>(officeHeaders.map(head => head.extraClasses));
  const { officeList, sort } = useSelector((state: RootState) => state.office);
  const debouncedSearch = useDebounce(searchTerm, 500);

  const onPageChange = (e: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    const trimmedSearch = debouncedSearch.trim();
    dispatch(
      getAllOffices({
        page: newPage, // Use 0-based page numbers
        size: sort.rowsPerPage,
        term: trimmedSearch.length ? trimmedSearch : undefined,
      })
    );
  };

  const onRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const trimmedSearch = debouncedSearch.trim();
    dispatch(
      getAllOffices({
        page: 0,
        size: parseInt(event.target.value, 10),
        term: trimmedSearch.length ? trimmedSearch : undefined,
      })
    );
  };

  const updateTable = () => {
    const trimmedSearch = debouncedSearch.trim();
    dispatch(
      getAllOffices({
        page: sort.pageNumber, // Use 0-based page numbers
        size: sort.rowsPerPage,
        term: trimmedSearch.length ? trimmedSearch : undefined,
      })
    );
  };

  const cancel = () => {
    setShowVerifyDialog(undefined);
    setShowDenyReason(false);
    setDenyReason("");
    updateTable();
  };

  const handleReason = async () => {
    if (showVerifyDialog?.officeId) {
      await dispatch(
        updateOfficeState({
          officeId: showVerifyDialog?.officeId,
          state: showDenyReason ? OfficeState.Rejected : OfficeState.Draft,
          reason: denyReason,
        })
      );
      cancel();
    }
  };

  const renderContent = () => {
    return (
      <div className="pb-5 overflow-hidden">
        <h2 className="uppercase font-bold">{showDenyReason ? "Please provide reason for denial." : "Office information"}</h2>
        <div className="mt-3">
          {!showDenyReason ? (
            <ul className="flex flex-col gap-3">
              <li>
                <p className="font-medium text-sm">Office Name</p>
                <p className="font-medium text-xl">{showVerifyDialog?.name}</p>
              </li>
              <li>
                <p className="font-medium text-sm">Office Address</p>
                <p className="font-medium text-xl">
                  {showVerifyDialog?.street2 ? `${showVerifyDialog?.street2}, ` : ""}
                  {showVerifyDialog?.street}, {showVerifyDialog?.state}, {showVerifyDialog?.zip}
                </p>
              </li>
              <li className="grid grid-cols-2">
                <div>
                  <p className="font-medium text-sm">Office Phone Number</p>
                  <p className="font-medium text-xl">{showVerifyDialog?.phone}</p>
                </div>
                <div>
                  <p className="font-medium text-sm">Office Fax Number</p>
                  <p className="font-medium text-xl">{showVerifyDialog?.fax}</p>
                </div>
              </li>
              <li className="grid grid-cols-2">
                <div>
                  <p className="font-medium text-sm">Primary Contact </p>
                  <p className="font-medium text-xl">{showVerifyDialog?.contactName}</p>
                </div>
                <div>
                  <p className="font-medium text-sm">Primary Contact Email </p>
                  <p className="font-medium text-xl !truncate">{showVerifyDialog?.contactEmail}</p>
                </div>
              </li>
            </ul>
          ) : (
            <>
              <TextInput
                isTextArea
                extraInputClass="resize-none"
                placeholder="Write details here"
                maxLength={250}
                counterMessage="250"
                showCounter
                value={denyReason}
                onChangeFunc={e => setDenyReason(e.target.value)}
              />
              <p>The office will be notified of this information.</p>
            </>
          )}
        </div>
        <div className="absolute bottom-7 right-7 flex justify-end gap-5 mt-5">
          {showDenyReason ? (
            <>
              <Button text="Cancel" variant="Outlined" onClick={() => cancel()} />
              <Button
                text="Deny"
                disabled={!denyReason.length}
                onClick={() => {
                  handleReason();
                }}
              />
            </>
          ) : (
            <>
              <Button text="Deny" variant="Outlined" onClick={() => setShowDenyReason(true)} />
            </>
          )}
        </div>
      </div>
    );
  };

  const handleVerifyClick = (e: React.MouseEvent<HTMLButtonElement>, item: Office) => {
    e.stopPropagation();
    setShowVerifyDialog(item);
  };

  useEffect(() => {
    const trimmedSearch = debouncedSearch.trim();
    dispatch(
      getAllOffices({
        page: 0,
        size: sort.rowsPerPage,
        term: trimmedSearch.length ? trimmedSearch : undefined,
      })
    );
  }, [dispatch, debouncedSearch, sort.rowsPerPage]);

  const offices = officeList.data?.content || [];

  if (offices.length)
    return (
      <div>
        <div className="flex justify-between items-center border-b border-docsigna-blue-light">
          {officeHeaders.map((head, index) => (
            <TableHeader
              title={head.title}
              index={index}
              key={index}
              sortIcon={null}
              handleSort={() => null}
              extraClassesHeader={head.extraClasses}
              isClickable={head.isClickable}
            />
          ))}
        </div>
        {offices.map((item, index) => {
          const rowData = {
            office: <RenderName title={item.name} subTitle="" />,
            city: [item?.street, item?.street1, item?.state, item?.city, item?.country].filter(Boolean).join(", "),
            date: moment(item.createdDate).format("ll"),
            status: item?.status ? item?.status : "-",
            numUsers: item?.numUsers,
            verify: (
              <button
                onClick={(e: React.MouseEvent<HTMLButtonElement>) => handleVerifyClick(e, item)}
                className="!pointer-events-auto text-sm text-center px-5 py-2.5 font-semibold leading-none rounded-full border shadow-none outline-none transition duration-500 text-white bg-docnote-orange hover:bg-white hover:text-docnote-orange border-docnote-orange"
              >
                Deny
              </button>
            ),
          };
          return (
            <TableRow
              data={Object.values(rowData)}
              key={index}
              id={""}
              tooltipVisible={false}
              handleOpenTooltip={() => null}
              handleCloseTooltip={() => null}
              tooltipOptions={[]}
              extraClassesRow={extraClasses}
              onRowClick={() => {
                navigate("/offices/" + item.officeId + "/requests");
              }}
            />
          );
        })}
        {officeList.data && (
          <Pagination
            colSpan={officeHeaders.length}
            count={officeList.data.totalElements}
            onPageChange={onPageChange}
            onRowsPerPageChange={onRowsPerPageChange}
            page={officeList.data.number} // Use 0-based page number
            rowsPerPage={officeList.data.size}
          />
        )}

        <ConfirmationDialog
          open={!!showVerifyDialog}
          description={renderContent()}
          showCrossIcon={!showDenyReason}
          title={""}
          canClickOutSide={false}
          successButtonText={""}
          handleSuccess={() => undefined}
          failureButtonText={""}
          handleFailure={() => setShowVerifyDialog(undefined)}
        />
      </div>
    );
  else return <p className="font-semibold">No offices found</p>;
};

export default AllOffice;
